// src/pages/Attendance/ManagerPendingApplications.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  approveOrRejectCancelLeaveApplication,
  approveOrRejectLeave,
  fetchManagerPendingApplications,
} from "../../store/slices/leaveSlice";
import LeaveCard from "./LeaveCard";
import RegularizationCard from "./RegularizationCard";
import { approveOrRejectRegularization } from "../../store/slices/leaveSlice";

const ManagerPendingApplications: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const { managerPendingApplications } = useSelector(
    (state: RootState) => state.leave
  );
  const [selectedApp, setSelectedApp] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchManagerPendingApplications());
  }, [dispatch]);

  if (!managerPendingApplications || managerPendingApplications.length === 0) {
    return null;
  }

  return (
    <div className="mb-8 p-5 bg-amber-50 border-l-4 border-amber-500 rounded-lg shadow-md">
      <div className="flex items-center mb-4">
        <div className="bg-amber-200 p-2 rounded-lg mr-3">
          {/* alert icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-amber-800"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-amber-900">
          Pending Leave Requests
          <span className="ml-2 bg-amber-600 text-white text-xs px-2 py-1 rounded-full">
            {managerPendingApplications.length}
          </span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {managerPendingApplications.map((app) => (
          <div
            key={app._id}
            className="bg-white p-3 rounded-lg shadow-sm border border-gray-300 hover:shadow-md transition-all"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-semibold text-gray-800 truncate max-w-[120px]">
                  {app.username
                    .split(" ")
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(" ")}
                </h3>
                <div className="flex items-center gap-1 mt-1">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      app.status === "approved"
                        ? "bg-green-600"
                        : app.status === "rejected"
                        ? "bg-red-600"
                        : "bg-yellow-600"
                    }`}
                  ></span>
                  <span className="text-xs text-gray-700">
                    {app.status === "cancel_pending"
                      ? "Leave Cancellation Request"
                      : app.applicationType === "leave"
                      ? `${
                          app.category?.charAt(0).toUpperCase() +
                          app.category?.slice(1)
                        } Leave`
                      : app.applicationType === "regularization"
                      ? "Attendance Regularization"
                      : "Comp-Off Grant"}
                  </span>
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full">
                Pending
              </span>
            </div>

            <div className="text-xs text-gray-700 mt-2 mb-3">
              {app.fromDate || app.dateEarned}
              {app.toDate && ` → ${app.toDate}`}
            </div>

            <div className="flex justify-between gap-2">
              <button
                className="flex-1 px-2 py-1.5 bg-green-400 hover:bg-green-600 text-white rounded text-xs transition-colors"
                onClick={() => {
                  if (app.applicationType === "regularization") {
                    dispatch(
                      approveOrRejectRegularization({
                        applicationId: app._id,
                        action: "approved",
                      })
                    );
                  } else if (app.status === "cancel_pending") {
                    dispatch(
                      approveOrRejectCancelLeaveApplication({
                        applicationId: app._id,
                        action: "approved",
                      })
                    );
                  } else {
                    dispatch(
                      approveOrRejectLeave({
                        applicationId: app._id,
                        action: "approved",
                      })
                    );
                  }
                }}
              >
                Approve
              </button>
              <button
                className="flex-1 px-2 py-1.5 bg-red-400 hover:bg-red-600 text-white rounded text-xs transition-colors"
                onClick={() => {
                  if (app.applicationType === "regularization") {
                    dispatch(
                      approveOrRejectRegularization({
                        applicationId: app._id,
                        action: "rejected",
                      })
                    );
                  } else if (app.status === "cancel_pending") {
                    dispatch(
                      approveOrRejectCancelLeaveApplication({
                        applicationId: app._id,
                        action: "rejected",
                      })
                    );
                  } else {
                    dispatch(
                      approveOrRejectLeave({
                        applicationId: app._id,
                        action: "rejected",
                      })
                    );
                  }
                }}
              >
                Reject
              </button>
              <button
                className="px-2 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-xs transition-colors"
                onClick={() => setSelectedApp(app)}
              >
                Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Details Modal */}
      {selectedApp && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedApp(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  Leave Request Details
                </h3>
                <button
                  className="text-gray-600 hover:text-gray-800"
                  onClick={() => setSelectedApp(null)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {selectedApp.applicationType === "regularization" ? (
                <RegularizationCard
                  application={selectedApp}
                  type="pending"
                  fromHomeTab={true}
                />
              ) : (
                <LeaveCard
                  application={selectedApp}
                  type="pending"
                  fromHomeTab={true}
                />
              )}

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-300">
                <button
                  className="px-4 py-2 bg-green-400 hover:bg-green-600 text-white rounded-lg text-sm"
                  onClick={() => {
                    if (selectedApp.applicationType === "regularization") {
                      dispatch(
                        approveOrRejectRegularization({
                          applicationId: selectedApp._id,
                          action: "approved",
                        })
                      );
                    } else if (selectedApp.status === "cancel_pending") {
                      dispatch(
                        approveOrRejectCancelLeaveApplication({
                          applicationId: selectedApp._id,
                          action: "approved",
                        })
                      );
                    } else {
                      dispatch(
                        approveOrRejectLeave({
                          applicationId: selectedApp._id,
                          action: "approved",
                        })
                      );
                    }
                    setSelectedApp(null);
                  }}
                >
                  Approve Request
                </button>
                <button
                  className="px-4 py-2 bg-red-400 hover:bg-red-600 text-white rounded-lg text-sm"
                  onClick={() => {
                    if (selectedApp.applicationType === "regularization") {
                      dispatch(
                        approveOrRejectRegularization({
                          applicationId: selectedApp._id,
                          action: "rejected",
                        })
                      );
                    } else if (selectedApp.status === "cancel_pending") {
                      dispatch(
                        approveOrRejectCancelLeaveApplication({
                          applicationId: selectedApp._id,
                          action: "rejected",
                        })
                      );
                    } else {
                      dispatch(
                        approveOrRejectLeave({
                          applicationId: selectedApp._id,
                          action: "rejected",
                        })
                      );
                    }
                    setSelectedApp(null);
                  }}
                >
                  Reject Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerPendingApplications;
